<script setup lang="ts">
/**
 * Platform homepage rendered at the apex domain (zerotohero.network/).
 * Tenant subdomains continue to show their own landing via TenantLanding.
 *
 * Strategy: one homepage, four vertical entry-points. Visitors self-select
 * the vertical that matches their business and land on a vertical-specific
 * page with the exact copy + features for them.
 *
 * Design: ink-on-paper with a single indigo accent — no gradient soup. The
 * hero's right column is a CSS-built product mock (a case pipeline with
 * stages and deadlines), because "your cases, finally organised" is the
 * pitch, so we show it instead of saying it.
 *
 * Chrome (nav + footer) comes from the 'marketing' layout, same as the
 * vertical landing pages and /pricing — don't render it here too.
 */
const verticals = [
  { slug: 'migration-agencies', label: 'Migration agencies', emoji: '🛂', desc: 'Visa subclasses, DHA evidence, bridging and lodgement dates, MARA compliance.' },
  { slug: 'education-agencies', label: 'Education agencies', emoji: '🎒', desc: 'Placements across destinations, provider offers and CoEs, commission ledger.' },
  { slug: 'law-firms', label: 'Law firms', emoji: '⚖️', desc: 'Matters, court dates, trust money, conflict checks, time to invoice.' },
  { slug: 'real-estate-agencies', label: 'Real estate agencies', emoji: '🏠', desc: 'Sales and leases from appraisal to settlement, key dates, deposits on trust.' }
]

// JSON-LD: tells Google we're an Organization, links logo, etc.
useStructuredData(organizationSchema())

// Default OG image + global SEO that index.vue may not have set in
// platform-mode contexts. SeoMeta is idempotent — overrides are fine.
useSeoMeta({
  title: 'Caseflow — Operations OS for small services agencies',
  description: 'Replace Excel, Word and Outlook with one tool that knows your industry on day one. Built for migration agencies, education agencies, law firms and real estate agencies.',
  ogTitle: 'Caseflow — Operations OS for small services agencies',
  ogDescription: 'One tool that knows your vertical on day one. Built for small services agencies.',
  ogImage: '/og-image.png',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterImage: '/og-image.png'
})

const pillars = [
  { title: 'Built for your vertical', icon: 'i-lucide-puzzle', desc: 'Migration, education, legal and real estate packs ship with terminology, fields, workflows, key dates and intake forms tuned to how your industry actually works.' },
  { title: 'Your data, your way', icon: 'i-lucide-sliders-horizontal', desc: 'Custom fields, custom workflows, custom forms. Make it yours without writing code.' },
  { title: 'The full money loop', icon: 'i-lucide-banknote', desc: 'Quotes → invoices → Stripe → Xero. Or a foreign personal account if that\'s how you work. Same workflow.' },
  { title: 'Magic-link client portal', icon: 'i-lucide-link-2', desc: 'Your clients never see a password. Documents, signatures, payments — all through one secure link.' },
  { title: 'Automations that ship with your pack', icon: 'i-lucide-zap', desc: 'Stage changes send the client email, create the follow-up task and tag the case — configured for your vertical out of the box.' },
  { title: 'AI where it earns its keep', icon: 'i-lucide-sparkles', desc: 'Two AI features: summarise a case in 4 sentences, or draft a reply from a pasted client email. No gimmicks.' }
]

// Hero product mock — the rows in the fake case list. Static on purpose:
// it's an illustration, not a demo.
const mockCases = [
  { name: 'Ana Silva', stage: 'Preparing', stageClass: 'bg-amber-100 text-amber-800', due: 'CoE due Fri' },
  { name: 'Tom Nguyen', stage: 'Lodged', stageClass: 'bg-indigo-100 text-indigo-800', due: 'RFI +28d' },
  { name: 'Mia Okafor', stage: 'Granted', stageClass: 'bg-emerald-100 text-emerald-800', due: 'Visa exp 2028' }
]
</script>

<template>
  <div class="bg-white text-gray-900">
    <!-- ── HERO ─────────────────────────────────────────────────── -->
    <section class="border-b border-gray-200">
      <div class="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 sm:py-28 lg:grid-cols-[6fr_5fr]">
        <div>
          <p class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">
            <span
              class="inline-block h-px w-8 bg-indigo-600"
              aria-hidden="true"
            />
            Operations OS for small services agencies
          </p>
          <h1 class="lp-display-archivo mt-6 text-5xl font-black tracking-tight text-gray-900 md:text-6xl">
            Replace Excel, Word and Outlook.
          </h1>
          <p class="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
            One tool that knows your industry on day one — cases, documents,
            deadlines, invoices and the client portal, without the spreadsheet
            juggling.
          </p>
          <div class="mt-8 flex flex-wrap gap-3">
            <NuxtLink
              to="/signup"
              class="rounded-lg bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Start 14-day free trial
            </NuxtLink>
            <NuxtLink
              to="/pricing"
              class="rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              See pricing
            </NuxtLink>
          </div>
          <p class="mt-4 text-xs text-gray-500">
            No credit card · CSV import from your Excel sheet in 5 minutes · cancel anytime
          </p>
        </div>

        <!-- Product mock: case pipeline -->
        <div
          class="relative mx-auto w-full max-w-md"
          aria-hidden="true"
        >
          <div class="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-indigo-600/10" />
          <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div class="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <span class="size-2.5 rounded-full bg-gray-300" />
              <span class="size-2.5 rounded-full bg-gray-300" />
              <span class="size-2.5 rounded-full bg-gray-300" />
              <span class="ml-3 text-xs font-medium text-gray-500">Cases — this week</span>
            </div>
            <div class="px-4 py-3">
              <!-- Stage stepper -->
              <div class="flex items-center gap-1">
                <template
                  v-for="(step, i) in ['Inquiry', 'Preparing', 'Lodged', 'Decision']"
                  :key="step"
                >
                  <span
                    class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    :class="i <= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'"
                  >{{ step }}</span>
                  <span
                    v-if="i < 3"
                    class="h-px w-3 bg-gray-300"
                  />
                </template>
              </div>
              <!-- Case rows -->
              <div class="mt-3 divide-y divide-gray-100">
                <div
                  v-for="c in mockCases"
                  :key="c.name"
                  class="flex items-center justify-between gap-3 py-2.5"
                >
                  <div class="flex items-center gap-2.5 min-w-0">
                    <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold text-gray-600">
                      {{ c.name.split(' ').map(w => w[0]).join('') }}
                    </span>
                    <span class="truncate text-sm font-medium text-gray-800">{{ c.name }}</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <span
                      class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      :class="c.stageClass"
                    >{{ c.stage }}</span>
                    <span class="text-[11px] text-gray-500">{{ c.due }}</span>
                  </div>
                </div>
              </div>
              <!-- Deadline strip -->
              <div class="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
                <span class="size-1.5 rounded-full bg-amber-500" />
                <span class="text-[11px] font-medium text-amber-800">2 deadlines this week — RFI response, CoE upload</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── VERTICAL CARDS ───────────────────────────────────────── -->
    <section class="mx-auto max-w-7xl px-6 py-20">
      <h2 class="lp-display-archivo text-3xl font-bold tracking-tight text-gray-900">
        Pick your vertical
      </h2>
      <p class="mt-3 max-w-2xl text-gray-600">
        Each pack ships with terminology, custom fields, workflows, key dates, an intake form and automations.
      </p>
      <div class="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <NuxtLink
          v-for="v in verticals"
          :key="v.slug"
          :to="`/${v.slug}`"
          class="group rounded-2xl border border-gray-200 bg-white p-6 transition hover:border-indigo-300 hover:shadow-md"
        >
          <div class="flex size-11 items-center justify-center rounded-xl bg-gray-100 text-2xl">{{ v.emoji }}</div>
          <h3 class="mt-4 font-semibold text-gray-900">{{ v.label }}</h3>
          <p class="mt-1 text-sm text-gray-600">{{ v.desc }}</p>
          <p class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600">
            See features
            <span class="transition-transform group-hover:translate-x-0.5">→</span>
          </p>
        </NuxtLink>
      </div>
    </section>

    <!-- ── PILLARS ──────────────────────────────────────────────── -->
    <section class="border-y border-gray-200 bg-gray-50 py-20">
      <div class="mx-auto max-w-7xl px-6">
        <h2 class="lp-display-archivo text-3xl font-bold tracking-tight text-gray-900">
          Why agencies choose Caseflow
        </h2>
        <div class="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="p in pillars"
            :key="p.title"
            class="rounded-xl border border-gray-200 bg-white p-6"
          >
            <UIcon
              :name="p.icon"
              class="size-6 text-indigo-600"
            />
            <h3 class="mt-3 font-semibold text-gray-900">
              {{ p.title }}
            </h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">
              {{ p.desc }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── TESTIMONIALS (mixed verticals) ───────────────────────── -->
    <TestimonialGrid
      :testimonials="featuredTestimonials()"
      heading="Loved by small services agencies"
      subheading="A peek at what early customers are saying."
      background="white"
    />

    <!-- ── PRICING STRIP ─────────────────────────────────────────── -->
    <section class="border-t border-gray-200">
      <div class="mx-auto max-w-5xl px-6 py-20 text-center">
        <h2 class="lp-display-archivo text-3xl font-bold tracking-tight text-gray-900">
          Simple, vertical-friendly pricing
        </h2>
        <p class="mt-3 text-gray-600">
          Pick the tier that fits your team. Move between tiers anytime.
        </p>
        <div class="mt-10 grid gap-5 text-left md:grid-cols-3">
          <div class="rounded-xl border border-gray-200 bg-white p-6">
            <p class="text-sm font-medium text-gray-500">
              Starter
            </p>
            <p class="mt-2 text-3xl font-bold text-gray-900">
              AUD $39<span class="text-base font-normal text-gray-500">/mo</span>
            </p>
            <ul class="mt-4 space-y-2 text-sm text-gray-700">
              <li>· 2 users</li>
              <li>· 50 active cases</li>
              <li>· 5 GB storage</li>
              <li>· Your vertical pack</li>
            </ul>
          </div>
          <div class="relative rounded-xl border-2 border-indigo-600 bg-white p-6">
            <span class="absolute -top-3 left-6 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Most popular</span>
            <p class="text-sm font-medium text-indigo-700">
              Growth
            </p>
            <p class="mt-2 text-3xl font-bold text-gray-900">
              AUD $99<span class="text-base font-normal text-gray-500">/mo</span>
            </p>
            <ul class="mt-4 space-y-2 text-sm text-gray-700">
              <li>· 5 users</li>
              <li>· 250 active cases</li>
              <li>· 25 GB storage</li>
              <li>· Custom workflows + branding</li>
              <li>· E-sign, Xero, Stripe</li>
              <li>· Automations + AI assistant</li>
            </ul>
          </div>
          <div class="rounded-xl border border-gray-200 bg-white p-6">
            <p class="text-sm font-medium text-gray-500">
              Scale
            </p>
            <p class="mt-2 text-3xl font-bold text-gray-900">
              AUD $249<span class="text-base font-normal text-gray-500">/mo</span>
            </p>
            <ul class="mt-4 space-y-2 text-sm text-gray-700">
              <li>· 15 users</li>
              <li>· Unlimited cases</li>
              <li>· 100 GB storage</li>
              <li>· Custom domain</li>
              <li>· API + webhooks</li>
              <li>· White-label portal</li>
            </ul>
          </div>
        </div>
        <NuxtLink
          to="/pricing"
          class="mt-8 inline-block text-sm font-medium text-indigo-600 hover:underline"
        >See full pricing comparison →</NuxtLink>
      </div>
    </section>

    <!-- ── BOTTOM CTA ───────────────────────────────────────────── -->
    <section class="bg-gray-900 py-20 text-white">
      <div class="mx-auto max-w-4xl px-6 text-center">
        <h2 class="lp-display-archivo text-3xl font-bold">
          Stop juggling spreadsheets.
        </h2>
        <p class="mt-3 text-white/80">
          Try Caseflow free for 14 days. We'll import your existing client list in under an hour.
        </p>
        <NuxtLink
          to="/signup"
          class="mt-8 inline-block rounded-lg bg-indigo-500 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-400"
        >
          Start your free trial
        </NuxtLink>
      </div>
    </section>
  </div>
</template>
